/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../shared/Utils/Utils';
import {
  ECartAt,
  IDetailComponentProps
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import AllUtilitiesSavingsByMeterDetailComponent from './AllUtilitiesSavingsByMeterDetailComponent';
import { Card } from '@scuf/common';
import AllUtilitiesSavingsBySiteDetailComponent from './AllUtilitiesCostSavingsBySiteDetailComponent';
import { ApiContext } from '../../../context/ApiContext';
import {
  IUtilityBillAllData,
  IUtilityCostAllData
} from '../UtilityBillViewDetailsComponent/IUtilityBillViewDetails';
type IUtilityMeter = IUtilityBillAllData | IUtilityCostAllData;
interface AllUtilitiesSavingsByMeterComponentContentProps {
  data: IUtilityMeter;
  onRefresh: () => Promise<void>;
  cartAtLabel?: ECartAt;
}

const AllUtilitiesSavingsByMeterComponentContent = (
  props: AllUtilitiesSavingsByMeterComponentContentProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, cartAtLabel } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div>
      {cartAtLabel === ECartAt.siteLabel ? (
        <AllUtilitiesSavingsByMeterDetailComponent
          data={data as IUtilityBillAllData}
        />
      ) : (
        <AllUtilitiesSavingsBySiteDetailComponent
          data={data as IUtilityCostAllData}
        />
      )}
    </div>
  );
};

const AllUtilitiesSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(AllUtilitiesSavingsByMeterComponentContent)
);

const AllUtilitiesSavingsByMeterComponent = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector } = props;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const allUtilitiesData = (all as unknown) as IUtilityMeter;
  const isDataAvailable = allUtilitiesData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityMeter);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(allUtilitiesData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  const apiContext = useContext(ApiContext);
  const cartAtLabel = apiContext.apiCallAt;
  const header =
    cartAtLabel === ECartAt.siteLabel
      ? t(d.BREAKDOWNOFUTILITY)
      : t(d.ALLUTILITYCOSTBYSITES, {
          count: (data as IUtilityCostAllData)?.breakdownBySite?.length || 0
        });

  return (
    <Card className="Savemeter">
      <Card.Header className="emaas-utility-view-details-all-utilities-savings-meter-card-header">
        {cartAtLabel === ECartAt.siteLabel ? (
          header
        ) : (
          <div className="emaas-site-count-header">
            <div>{header}</div>
            <div>{t(d.SITEWITHDATA)}</div>
          </div>
        )}
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable">
          <AllUtilitiesSavingsByMeterComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            cartAtLabel={cartAtLabel}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default AllUtilitiesSavingsByMeterComponent;
